import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Check, X, Trash2, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { friendlyError } from "@/lib/errors";

/**
 * /admin/directory — review pending directory applications and manage
 * approved listings.
 */
export default function AdminDirectory() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/admin/directory", { params });
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load the applications."));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    pending: rows.filter((r) => r.status === "pending").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  }), [rows]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/directory/${id}/approve`);
      toast.success("Approved — the listing is now public.");
      setRows((r) => r.map((row) => row.id === id ? { ...row, status: "approved" } : row));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setBusyId(null); }
  };
  const reject = async (id) => {
     
    const note = window.prompt("Reason for rejection (optional, shown to no one):", "");
    if (note === null) return;
    setBusyId(id);
    try {
      await api.post(`/admin/directory/${id}/reject`, { note });
      toast.success("Rejected — the listing is hidden.");
      setRows((r) => r.map((row) => row.id === id ? { ...row, status: "rejected", rejection_note: note } : row));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setBusyId(null); }
  };
  const remove = async (id) => {
     
    if (!window.confirm("Permanently delete this application?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/directory/${id}`);
      toast.success("Deleted.");
      setRows((r) => r.filter((row) => row.id !== id));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setBusyId(null); }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-14 text-center">
          <h1 className="font-display text-xl font-bold mb-2">Admin only</h1>
          <Link to="/" className="btn-primary inline-block mt-4">Back to home</Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-directory-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
            <Users className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-3xl font-extrabold">Directory Applications</h1>
            <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
              Review applications from attorneys and paralegals who want to be listed in
              the public assistants directory.
            </p>
          </div>
          <Link to="/directory" className="btn-ghost text-sm">Public directory →</Link>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {[["pending", `Pending · ${counts.pending}`],
            ["approved", `Approved · ${counts.approved}`],
            ["rejected", `Rejected · ${counts.rejected}`],
            ["all", "All"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setStatusFilter(k)}
              className={statusFilter === k ? "btn-primary text-xs" : "btn-ghost text-xs"}
              data-testid={`admin-directory-filter-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="card-surface overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
              No applications match this filter.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-stone)" }} data-testid="admin-directory-list">
              {rows.map((r) => (
                <ApplicationRow
                  key={r.id}
                  row={r}
                  busy={busyId === r.id}
                  onApprove={() => approve(r.id)}
                  onReject={() => reject(r.id)}
                  onDelete={() => remove(r.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function ApplicationRow({ row, busy, onApprove, onReject, onDelete }) {
  const [open, setOpen] = useState(row.status === "pending");
  const statusColor = row.status === "approved" ? "pill-ok"
    : row.status === "rejected" ? "pill-bad" : "pill-warn";
  return (
    <li className="p-4" data-testid={`admin-directory-row-${row.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-display font-bold text-sm" style={{ color: "var(--text-primary)" }}>{row.full_name}</span>
            <span className={`pill ${statusColor}`}>{row.status}</span>
            <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>{row.type}</span>
          </div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {row.firm_name && <>{row.firm_name} · </>}
            {row.city}, {row.state} · {row.email}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOpen((v) => !v)} className="btn-ghost text-xs">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {row.status !== "approved" && (
            <button type="button" onClick={onApprove} disabled={busy}
                    className="btn-primary text-xs"
                    data-testid={`approve-${row.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : <Check className="w-3.5 h-3.5 inline mr-1" />}
              Approve
            </button>
          )}
          {row.status !== "rejected" && (
            <button type="button" onClick={onReject} disabled={busy}
                    className="btn-ghost text-xs"
                    data-testid={`reject-${row.id}`}>
              <X className="w-3.5 h-3.5 inline mr-1" />Reject
            </button>
          )}
          <button type="button" onClick={onDelete} disabled={busy}
                  className="btn-ghost text-xs"
                  data-testid={`delete-${row.id}`}
                  title="Permanently delete">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t text-xs grid sm:grid-cols-2 gap-2"
             style={{ borderColor: "var(--border-stone)", color: "var(--text-primary)" }}>
          <div><strong>Phone:</strong> {row.phone}</div>
          {row.text_number && <div><strong>Text:</strong> {row.text_number}</div>}
          <div className="sm:col-span-2"><strong>Address:</strong> {row.address}, {row.city}, {row.state} {row.zip}</div>
          {row.languages && row.languages.length > 0 && (
            <div className="sm:col-span-2"><strong>Languages:</strong> {row.languages.join(", ")}</div>
          )}
          {row.website && <div className="sm:col-span-2"><strong>Website:</strong> {row.website}</div>}
          {row.type === "attorney" && (
            <div className="sm:col-span-2">
              <strong>Bar:</strong> {row.bar_state} #{row.bar_number} · admitted {row.bar_admission_date}
            </div>
          )}
          {row.type === "paralegal" && (
            <>
              {row.years_experience != null && <div><strong>Years:</strong> {row.years_experience}</div>}
              {row.services_offered && row.services_offered.length > 0 && (
                <div className="sm:col-span-2"><strong>Services:</strong> {row.services_offered.join(", ")}</div>
              )}
            </>
          )}
          {row.bio && <div className="sm:col-span-2"><strong>Bio:</strong> {row.bio}</div>}
          <div className="sm:col-span-2 italic" style={{ color: "var(--text-secondary)" }}>
            Submitted {row.submitted_at?.slice(0, 10) || "—"}
            {row.approved_at && ` · approved ${row.approved_at.slice(0, 10)} by ${row.approved_by || ""}`}
            {row.rejection_note && ` · rejected note: “${row.rejection_note}”`}
          </div>
        </div>
      )}
    </li>
  );
}
